import { Box, Text } from '@factorialco/deprecated-design-system'
import { Spinner } from '@factorialco/f0-react/dist/experimental'
import { Features } from 'generated/featureDefinitions'
import {
  ContractHistoryDocument,
  ContractVersionPageContractVersionFragment,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { i18nDateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { isFeatureAvailabilityEnabled } from 'modules/core/lib/isFeatureAvailabilityEnabled'

import ContractVersionCard from 'modules/contracts/components/ContractHistory/ContractVersionCard'
import type EmployeeContract from 'modules/contracts/lib/EmployeeContract'

import { Milestone } from './Timeline/Milestone'

interface Props {
  employeeContract?: EmployeeContract
  selectedContractVersion: ContractVersionPageContractVersionFragment
  employeeId: string
  jobCatalogNodeUuids: string[]
  setSelectedGraphqlContractVersion: (contractVersionId: string | undefined) => void
}

interface ContractStartMilestoneProps {
  label?: string
}

const ContractEndMilestone = ({ label }: ContractStartMilestoneProps) => {
  return (
    <Milestone color="grey600" label={label}>
      <Box gap="s8">
        <Box flexDirection="row" gap="s8" alignItems="center">
          <Text.Normal weight="semibold" size="200" color="grey700">
            {i18n.t('contract_versions.navigator.timeline.terminated')}
          </Text.Normal>
        </Box>
      </Box>
    </Milestone>
  )
}

const ContractHistory = ({
  employeeContract,
  selectedContractVersion,
  employeeId,
  jobCatalogNodeUuids,
  setSelectedGraphqlContractVersion,
}: Props) => {
  const { company } = useCurrentSession()

  const { data, loading } = useQuery(ContractHistoryDocument, {
    variables: {
      nodeUuids: jobCatalogNodeUuids,
      hasJobCatalogNodes: jobCatalogNodeUuids.length > 0,
      companyId: company.id,
      employeeId,
      featureAvailabilityIds: [
        Features.compensations_management,
        Features.time_tracking_annualisation_daily_value,
      ],
    },
  })

  const contractVersions = data?.contracts?.contractVersionsConnection?.nodes
  const hasLegacyJobCatalog =
    data?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? false
  const hasDevJobCatalogTree = !hasLegacyJobCatalog
  const hasDevTimeTrackingWorkableDaysDefinition = data?.features?.featuresConnection?.nodes?.find(
    (feature) => feature.name === 'dev_time_tracking_workable_days_definition'
  )?.enabled

  const featureAvailabilities = useUnsafeMemo(
    () => data?.companies?.featureAvailabilitiesConnection?.nodes || [],
    [data]
  )

  const hasTimeTrackingAnnualisationDailyValue =
    data?.companies?.featureAvailabilitiesConnection?.nodes?.find(
      (feature) => feature.id === Features.time_tracking_annualisation_daily_value
    )?.available

  const hasCompensations = isFeatureAvailabilityEnabled(
    featureAvailabilities,
    Features.compensations_management
  )

  const label =
    employeeContract?.hasEndDate() && !!employeeContract.endsOn
      ? i18nDateToString(employeeContract.endsOn, 'ui_dmy_long')
      : undefined

  if (loading) {
    return (
      <Box height="full" alignItems="center" justifyContent="center" paddingRight="s80">
        <Spinner />
      </Box>
    )
  }

  const contractVersionIsExpired =
    employeeContract?.endsOn && employeeContract.endsOn.isBefore(moment())

  // Filter contractVersions to only show those that belong to this contract band
  const contractBandVersionIds = employeeContract?.versions?.map((v) => v.id) || []
  const filteredContractVersions =
    contractVersions?.filter((cv) => cv.id && contractBandVersionIds.includes(cv.id)) || []

  // For schedule calculation, we need to pass all contractVersions, not just the filtered ones
  // This ensures getContractVersionSchedule works correctly

  return (
    <Box gap="s8">
      {contractVersionIsExpired && <ContractEndMilestone label={label} />}
      {filteredContractVersions.map((contractVersion, index) => {
        if (!contractVersion.id) return null

        return (
          <ContractVersionCard
            key={contractVersion.id}
            contractVersion={contractVersion}
            contractVersions={contractVersions || []}
            contractVersionChanges={contractVersion.contractVersionChanges}
            highlightedItemId={selectedContractVersion.id ?? undefined}
            setSelectedGraphqlContractVersion={setSelectedGraphqlContractVersion}
            hasPreviousContract={!!filteredContractVersions[index + 1]}
            hasDevJobCatalogTree={!!hasDevJobCatalogTree}
            hasTimeTrackingAnnualisationDailyValue={!!hasTimeTrackingAnnualisationDailyValue}
            hasDevTimeTrackingWorkableDaysDefinition={!!hasDevTimeTrackingWorkableDaysDefinition}
            jobCatalogTreeLevelNodes={data?.jobCatalog?.treeNodesConnection?.nodes || []}
            hasCompensationsManagement={hasCompensations}
          />
        )
      })}
    </Box>
  )
}

export default ContractHistory
